import React, {Component} from 'react';
import {get, max, flatten, map} from 'lodash';
import PageHeader from './PageHeader';
import SurveyEditorItem from './SurveyEditorItem';
import ItemTypePicker from './ItemTypePicker';
import ItemCopyFromPicker from './ItemCopyFromPicker';
import pageBuilders from '../../../services/pageBuilders';
import pageCloners from '../../../services/pageCloners';
import {generateNextPageId, generateNextQuestionId, toIntegerArray} from '../../../services/idHelpers';
import getIntl from '../../../services/getIntl';
import PageTypeItem from './PageTypeItem';
import {DragDropContext, Droppable, Draggable} from 'react-beautiful-dnd';
import {getBackgroundColor} from '../../../utils/drag-and-drop';

class PageType extends Component {
    constructor(props) {
        super(props);

        const {form, name} = props;
        this.state = {count: get(form.values, name) ? get(form.values, name).length : 0,
            expandedPages: new Set()};

        this.addItem = this.addItem.bind(this);
        this.copyFromItem = this.copyFromItem.bind(this);
        this.addJump = this.addJump.bind(this);
        this.clonePage = this.clonePage.bind(this);
        this.handleDrop = this.handleDrop.bind(this);
        this.handleExpandedPage = this.handleExpandedPage.bind(this);
        this.remove = this.remove.bind(this);
    }

    addItem(type) {
        const {form, name} = this.props;

        const nextPageId = () => generateNextPageId(form.values.editor);
        const nextId = () => generateNextQuestionId(form.values.editor);

        const newPage = pageBuilders[type](nextPageId, nextId, getIntl(form.values.locale));

        const {expandedPages} = this.state;
        const values = get(form.values, name);
        expandedPages.add(values ? values.length : 0);
        this.setState({count: values ? values.length : 0, expandedPages});
        this.props.push(newPage);
    }

    copyFromItem(pages) {
        const {form} = this.props;
        const startingPage =
            (max(toIntegerArray(form.values.editor.pages.map((page) => page.id))) || 0) + 1;
        const startingQuestion =
            ((max(toIntegerArray(flatten(form.values.editor.pages.map((page) => flatten(page.items).map((item) => item.id))))) || 0) + 1);
        pages.forEach(async (page, index) => {
            const nextPageId = () => `p${startingPage + index}`;
            const nextId = () => `${startingQuestion + index}`;
            const newPage = pageCloners[page.items[0].type](nextPageId, nextId, page);
            await this.props.push(newPage);
        });
    }

    clonePage(page, index) {
        const {form, name} = this.props;

        const nextPageId = () => generateNextPageId(form.values.editor);
        const nextId = () => generateNextQuestionId(form.values.editor);
        const newPage = pageCloners[page.items[0].type](nextPageId, nextId, page);
        this.props.insert(index, newPage);

        const values = get(form.values, name);
        const count = values ? values.length + 1 : 0;
        // commented because cloned page is never expanded
        // const {expandedPages} = this.state;
        // if (index === count) { // expanded only if it's last index
        //     expandedPages.add(index);
        // }
        this.setState({count/* , expandedPages */});
    }

    addJump() {
        this.props.push({expression: [], jumpTo: ''});
    }

    handleDrop(droppedItem) {
        // Ignore drop outside droppable container
        if (!droppedItem.destination) {
            return;
        }
        this.props.move(droppedItem.source.index, droppedItem.destination.index);
    }

    handleExpandedPage(index, expanded) {
        const {expandedPages} = this.state;
        if (expanded) {
            expandedPages.add(index);
        } else {
            expandedPages.delete(index);
        }
        this.setState({expandedPages});
    }

    remove(index) {
        this.props.remove(index);
        const {expandedPages} = this.state;
        expandedPages.delete(index);
        this.setState({expandedPages});
    }

    render() {
        const {move, replace, form, name} = this.props;
        let values = get(form.values, name) || [];
        // auto fix values if missing
        values = values.map((page) => {
            if (page.enableWidget !== false) {
                page.enableWidget = true;
            }
            return page;
        });
        const len = values.length;
        return (
            <>
                <DragDropContext onDragEnd={this.handleDrop}>
                    <Droppable droppableId="list-container">
                        {(provided, snapshot) => (
                            <div
                                className="questions-container"
                                {...provided.droppableProps}
                                ref={provided.innerRef}
                                style={{background: getBackgroundColor(snapshot)}}
                            >
                                {values.map((page, index) => {
                                    return (
                                        <Draggable
                                            key={`page-${index}`}
                                            draggableId={`page-${index}`}
                                            index={index}
                                            isDragDisabled={this.state.expandedPages.size > 0}
                                        >
                                            {(provided, snapshot) => (
                                                <div
                                                    ref={provided.innerRef}
                                                    {...provided.dragHandleProps}
                                                    {...provided.draggableProps}
                                                >
                                                    <SurveyEditorItem
                                                        key={index} index={index} expanded={index === len - 1 && len > this.state.count}
                                                        handleExpanded={this.handleExpandedPage}
                                                        header={
                                                            <PageHeader item={page} index={index} len={len}
                                                                onClonePage={this.clonePage} remove={this.remove} move={move} replace={replace} />
                                                        }>
                                                        <PageTypeItem index={index} item={page} pageCount={len}
                                                            items={flatten(map(form.values.editor.pages, 'items'))}
                                                            parameters={form.values.parameters} extraFields={form.values.extraFields} />
                                                    </SurveyEditorItem>
                                                </div>
                                            )}
                                        </Draggable>);
                                })}
                                {provided.placeholder}
                            </div>
                        )}
                    </Droppable>
                </DragDropContext>
                <div className="row">
                    <div className="col-md-6 col-12">
                        <ItemTypePicker onItemClicked={this.addItem} />
                    </div>
                    <div className="col-md-6 col-12">
                        <ItemCopyFromPicker onItemClicked={this.copyFromItem} />
                    </div>
                </div>
            </>
        );
    }
}

export default PageType;

